import type { SkillId } from '../types/ids';

/**
 * Skill IDs are hierarchical, stable and shared across games (architecture §14):
 * dot-separated segments of lowercase alphanumerics and single hyphens, at least two
 * segments — e.g. `math.addition.within-10`.
 *
 * This lives in src/shared so game modules can validate their own content without importing
 * platform code (architecture §4 dependency rule). src/platform/skills re-exports it.
 */
const SEGMENT = '[a-z0-9]+(?:-[a-z0-9]+)*';

export const SKILL_ID_PATTERN = new RegExp(`^${SEGMENT}(?:\\.${SEGMENT})+$`);

export function isValidSkillId(value: string): value is SkillId {
  return SKILL_ID_PATTERN.test(value);
}

/**
 * The grammar every *governed* identifier has to obey to be persisted (S1-3).
 *
 * Skill ids have their own hierarchical shape above. Everything else this build files under an id
 * — a content id, a scoring event id, a mission id — is generated from authored ids, lane ids and
 * UUIDs, so it is letters, digits, dots, hyphens and underscores and nothing else. The point is
 * not that the character set is interesting: it is that these fields used to accept arbitrary
 * caller text, and arbitrary text under our own namespace is where a data URL, a coordinate series
 * or a child's name hides (D-028, architecture §19). The length cap is part of the same rule —
 * an id long enough to hold a payload is not an id.
 */
const GOVERNED_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

export const MAX_GOVERNED_ID_LENGTH = 128;

export function isGovernedId(value: string): boolean {
  return value.length <= MAX_GOVERNED_ID_LENGTH && GOVERNED_ID_PATTERN.test(value);
}

export class InvalidSkillIdError extends Error {
  readonly value: string;

  constructor(value: string) {
    super(
      `Invalid skill ID "${value}". Expected dot-separated lowercase segments, e.g. "math.addition.within-10".`,
    );
    this.name = 'InvalidSkillIdError';
    this.value = value;
  }
}

export function assertValidSkillId(value: string): asserts value is SkillId {
  if (!isValidSkillId(value)) throw new InvalidSkillIdError(value);
}

/** Splits a validated skill ID into its segments. Throws on an invalid ID. */
export function parseSkillId(value: string): string[] {
  assertValidSkillId(value);
  return value.split('.');
}

/** Top-level domain segment, e.g. `math` for `math.addition.within-10`. */
export function skillDomain(value: SkillId): string {
  const [domain] = parseSkillId(value);
  // parseSkillId guarantees at least two segments.
  return domain as string;
}
